import React, { useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from './Auth';
import axios from 'axios';
import { appContext } from '../../App';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faEnvelope, faLock, faUser, faArrowRight, faCheckCircle, faMoon, faSun, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const SignUpScreen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const loginPath = `/login${location.search}`;
    const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
    const [visibleConfirmPassword, setVisibleConfirmPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [resendLoading, setResendLoading] = useState(false);

    const { darkMode, setDarkMode } = useContext(appContext);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleResendEmail = async () => {
        setResendLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
            await sendEmailVerification(userCredential.user);
            await signOut(auth);
            setSuccessMessage("Verification email resent!");
        } catch (error) {
            console.error("Error resending email:", error);
            setErrorMessage("Failed to resend email. Please try again later.");
        } finally {
            setResendLoading(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage(null);

        const validations = [
            {
                condition: !formData.username.trim() || !formData.email.trim(),
                message: 'Username and email cannot be empty',
            },
            {
                condition: !/^[A-Za-z0-9]+$/.test(formData.username),
                message: 'Username can only contain letters and numbers',
            },
            {
                condition: formData.password.length < 8,
                message: 'Password must be at least 8 characters long',
            },
            {
                condition: formData.password !== formData.confirmPassword,
                message: "Passwords don't match",
            },
            {
                condition: !termsAccepted,
                message: 'Please accept the terms and conditions',
            },
        ];

        for (const validation of validations) {
            if (validation.condition) {
                setErrorMessage(validation.message);
                return;
            }
        }

        setIsLoading(true);
        try {
            const response = await axios.post('/pool/auth/register', {
                userName: formData.username,
                email: formData.email,
                password: formData.password,
            });
            if (response.data.status) {
                try {
                    const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
                    await sendEmailVerification(userCredential.user);
                    await signOut(auth);
                    setIsSuccess(true);
                } catch (firebaseError: any) {
                    console.error("Firebase auth error during signup:", firebaseError);
                    setErrorMessage("Account created, but failed to send verification email. Please try logging in.");
                    setTimeout(() => navigate(loginPath), 3000);
                }
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'An error occurred while processing your request');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setErrorMessage(null);
        setSuccessMessage(null);
    };

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-slate-950 transition-colors duration-500 overflow-hidden">
            {/* Left side: branding (Hidden on small screens) */}
            <div className="hidden md:flex md:w-1/2 lg:w-3/5 bg-[#0c2a4a] relative overflow-hidden items-center justify-center p-12">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[120px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-400/10 rounded-full blur-[150px] animate-pulse"></div>

                <div className="relative z-10 max-w-lg text-center">
                    <img src="/assets/segpool.png" alt="SegPool Logo" className="h-20 mx-auto mb-8 drop-shadow-2xl brightness-110" />
                    <h1 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
                        Join the <span className="text-blue-400">Segments</span> Network
                    </h1>
                    <p className="text-blue-100/70 text-lg lg:text-xl font-medium leading-relaxed">
                        Start mining with the most advanced pool features and professional tools designed for scalability.
                    </p>
                </div>
            </div>

            {/* Right side: Signup Form */}
            <div className="flex-1 flex flex-col relative overflow-y-auto bg-white dark:bg-slate-950/40">
                <div className="p-4 md:p-6 flex items-center z-50">
                    {/* <div className="flex-1 md:hidden"></div> */}
                        <div className="flex-1 flex justify-end gap-2">
                            <Link
                                to="/"
                                className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-center"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
                            </Link>
                            <button
                                onClick={() => setDarkMode(!darkMode)}
                                className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-800 shadow-sm"
                            >
                                <FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="text-lg" />
                            </button>
                        </div>
                </div>

                <div className="flex-1 flex items-center justify-center p-6 md:p-12 lg:p-20">
                    <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-8 duration-700">
                        {isSuccess ? (
                            <div className="text-center">
                                <div className="w-16 h-16 md:w-24 md:h-24 bg-green-500/10 rounded-full flex items-center justify-center mb-6 md:mb-8 mx-auto ring-4 ring-green-500/20">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-3xl md:text-5xl text-green-500" />
                                </div>
                                <h2 className="text-3xl md:text-4xl font-black text-[#0c2a4a] dark:text-white mb-4 tracking-tight">You're All Set!</h2>
                                <p className="text-slate-600 dark:text-slate-400 text-sm md:text-lg mb-8 md:mb-10 leading-relaxed font-black uppercase tracking-widest opacity-80">
                                    Verify your email <br /><span className="text-blue-600 dark:text-blue-400 font-black break-all">{formData.email}</span>
                                </p>

                                <div className="space-y-4">
                                    <button
                                        onClick={() => navigate(loginPath)}
                                        className="w-full py-3.5 md:py-4 bg-[#0A3A6B] text-white rounded-2xl font-black text-base md:text-lg hover:bg-[#0c4a8a] transition-all flex items-center justify-center gap-3 h-[54px] md:h-[60px]"
                                    >
                                        <span>CONTINUE TO LOGIN</span>
                                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                    </button>

                                    <div className="pt-4">
                                        <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400 font-bold mb-2 uppercase tracking-widest">Didn't receive it?</p>
                                        <button
                                            onClick={handleResendEmail}
                                            disabled={resendLoading}
                                            className="text-blue-600 dark:text-blue-400 font-black hover:underline disabled:opacity-50 text-xs md:text-sm"
                                        >
                                            {resendLoading ? "SENDING..." : "RESEND EMAIL"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mb-6 md:mb-10 text-center md:text-left">
                                    <img src="/assets/segmentslogo.png" alt="SegPool Logo" className="md:hidden h-14 mx-auto mb-3 drop-shadow-2xl brightness-110" />
                                    <h2 className="text-3xl md:text-4xl font-black text-[#0c2a4a] dark:text-white mb-2 tracking-tight">Create Account</h2>
                                    <p className="text-slate-500 dark:text-slate-400 font-medium text-[10px] md:text-base uppercase tracking-widest opacity-80">Join thousands of professional miners today.</p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                                    <div className="space-y-1 md:space-y-2">
                                        <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest ml-1">Username</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                                <FontAwesomeIcon icon={faUser} className="text-xs md:text-sm" />
                                            </div>
                                            <input
                                                className="w-full pl-11 pr-4 py-3 md:py-3.5 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all font-medium text-sm md:text-base"
                                                type="text"
                                                name="username"
                                                placeholder="username"
                                                value={formData.username.toLowerCase()}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1 md:space-y-2">
                                        <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                                <FontAwesomeIcon icon={faEnvelope} className="text-xs md:text-sm" />
                                            </div>
                                            <input
                                                className="w-full pl-11 pr-4 py-3 md:py-3.5 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all font-medium text-sm md:text-base"
                                                type="email"
                                                name="email"
                                                placeholder="miner@example.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1 md:space-y-2">
                                            <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest ml-1">Password</label>
                                            <div className="relative group">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                                    <FontAwesomeIcon icon={faLock} className="text-xs md:text-sm" />
                                                </div>
                                                <input
                                                    className="w-full pl-11 pr-10 py-3 md:py-3.5 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all font-medium text-sm md:text-base"
                                                    type={visiblePassword ? 'text' : 'password'}
                                                    name="password"
                                                    placeholder="••••••••"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                                                    onClick={() => setVisiblePassword(!visiblePassword)}
                                                >
                                                    <FontAwesomeIcon icon={visiblePassword ? faEyeSlash : faEye} className="text-xs md:text-sm" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-1 md:space-y-2">
                                            <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest ml-1">Confirm</label>
                                            <div className="relative group">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                                    <FontAwesomeIcon icon={faLock} className="text-xs md:text-sm" />
                                                </div>
                                                <input
                                                    className="w-full pl-11 pr-10 py-3 md:py-3.5 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all font-medium text-sm md:text-base"
                                                    type={visibleConfirmPassword ? 'text' : 'password'}
                                                    name="confirmPassword"
                                                    placeholder="••••••••"
                                                    value={formData.confirmPassword}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                                                    onClick={() => setVisibleConfirmPassword(!visibleConfirmPassword)}
                                                >
                                                    <FontAwesomeIcon icon={visibleConfirmPassword ? faEyeSlash : faEye} className="text-xs md:text-sm" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-3 px-1 pt-1 md:pt-2">
                                        <input
                                            id="terms"
                                            type="checkbox"
                                            checked={termsAccepted}
                                            onChange={(e) => setTermsAccepted(e.target.checked)}
                                            className="w-4 h-4 md:w-5 md:h-5 rounded border-slate-300 dark:border-slate-700 text-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-900 transition-all cursor-pointer"
                                        />
                                        <label htmlFor="terms" className="text-[10px] md:text-sm font-bold text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                                            I accept the <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">Terms of Service</a>
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isLoading || !termsAccepted}
                                        className="w-full py-3.5 md:py-4 bg-[#0A3A6B] hover:bg-[#0c4a8a] text-white rounded-2xl font-black text-base md:text-lg shadow-xl shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98] transition-all disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-3 mt-4 md:mt-6 h-[54px] md:h-[60px]"
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                <span className="text-sm md:text-base tracking-widest uppercase">CREATING ACCOUNT...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <span>CREATE ACCOUNT</span>
                                                <FontAwesomeIcon icon={faArrowRight} className="text-xs opacity-70" />
                                            </>
                                        )}
                                    </button>
                                </form>

                                <div className="mt-8 md:mt-10 text-center">
                                    <p className="text-slate-500 dark:text-slate-400 font-bold text-[10px] md:text-sm uppercase tracking-widest">
                                        Already have an account?{' '}
                                        <button
                                            onClick={() => navigate(loginPath)}
                                            className="text-blue-600 dark:text-blue-400 font-black hover:underline"
                                        >
                                            Sign in
                                        </button>
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Notifications */}
            {(errorMessage || successMessage) && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 md:left-auto md:right-8 md:translate-x-0 z-[100] animate-in slide-in-from-bottom-10 duration-500 w-[calc(100%-2rem)] max-w-sm">
                    <div className={`${successMessage
                        ? 'bg-green-500'
                        : 'bg-red-500'
                        } text-white p-4 rounded-2xl shadow-2xl flex items-center justify-between border-4 border-white/10`}
                    >
                        <p className="font-bold pr-4 flex-1 text-xs md:text-sm">{successMessage || errorMessage}</p>
                        <button
                            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                            onClick={handleCloseSnackbar}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignUpScreen;
